import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { notification } from "antd";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { RepresentationModal } from "@/features/representation-modal";
import { SettingsUsersTable } from "@/features/settings-users-table";
import { queryClient } from "@/shared/lib/react-query";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";
import { journalsQueries } from "@entities/journal";
import "./single-table-module.scss";

export const AdministratorSettingsCard = ({
  mainJournalId,
}: {
  mainJournalId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: responsibleList,
    isLoading,
    isFetching,
  } = useQuery(journalsQueries.getResponsibleAdminList({ mainJournalId }));
  const { mutateAsync } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/main/{mainJournalId}/admin",
  );
  const { mutateAsync: deleteAsync } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/main/{mainJournalId}/admin",
  );

  const handleAddUser = (userId: string) => {
    mutateAsync({
      params: { path: { mainJournalId } },
      body: { userId },
    })
      .then(() => {
        setIsOpen(false);
        notification.success({
          description: NOTIFICATION_MESSAGES.JOURNAL_ADMIN_ADDED_DESC, // "Администратор добавлен"
          message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleAdminList({
            mainJournalId,
          }).queryKey,
        });
      })
      .catch((r) => {
        notification.error({
          description: r.errorDescription,
          message: NOTIFICATION_MESSAGES.JOURNAL_ADMIN_ADD_ERROR, // "Ошибка при добавлении администратора"
        });
      });
  };

  const handleDeleteUser = (userId: string) => {
    deleteAsync({
      params: { path: { mainJournalId } },
      body: { userId },
    })
      .then(() => {
        setIsOpen(false);
        notification.success({
          description: NOTIFICATION_MESSAGES.JOURNAL_ADMIN_DELETED_DESC, // "Администратор удален"
          message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleAdminList({
            mainJournalId,
          }).queryKey,
        });
      })
      .catch((r) => {
        notification.error({
          description: r.errorDescription,
          message: NOTIFICATION_MESSAGES.JOURNAL_ADMIN_DELETE_ERROR, // "Ошибка при удалении администратора"
        });
      });
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  if (isLoading) return <SettingsUsersTable title="Администратор журнала" isLoading />;

  return (
    <>
      <SettingsUsersTable
        title="Администратор журнала"
        users={
          responsibleList?.map((u) => ({
            id: String(u.userHistory?.userId || ""),
            fullName: `${u.userHistory?.userSurname || ""} ${u.userHistory?.userFirstName || ""} ${u.userHistory?.userMiddleName || ""}`.trim(),
            organization: u.userHistory?.roleInfo?.userCompaniesHistory?.[0]?.shortName,
            position: u.userHistory?.roleInfo?.userCompaniesHistory?.[0]?.position,
            email: u.userHistory?.userEmail || undefined,
            phone: u.userHistory?.userMobilePhone || undefined,
            isActive: u.userHistory?.roleInfo?.isCurrently !== false,
          })) || []
        }
        isLoading={isLoading}
        isFetching={isFetching}
        canManageAcl="main_journals"
        onAddClick={handleOpenModal}
        onDeleteUser={(id: string) => handleDeleteUser(id)}
        onRestoreUser={(id: string) => handleAddUser(id)}
      />
      <RepresentationModal
        handleAdd={(r) => handleAddUser(r as string)}
        isOpen={isOpen}
        handleClose={handleCloseModal}
      />
    </>
  );
};
